import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  getWriteTaskStatus,
  getWriteTaskList,
  rollbackProdWrite,
  type WriteTaskStatus,
  type WriteTaskListItem,
} from '../../services/data-integration-api';

interface ProdWriteTaskProps {
  writeTaskId: string;
  onBack?: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  running: '执行中',
  completed: '已完成',
  failed: '失败',
  partial: '部分完成',
  rolling_back: '回滚中',
  rolled_back: '已回滚',
};

const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  running: 'secondary',
  completed: 'default',
  failed: 'destructive',
  partial: 'outline',
  rolling_back: 'secondary',
  rolled_back: 'outline',
};

export default function ProdWriteTask({ writeTaskId, onBack }: ProdWriteTaskProps) {
  const [task, setTask] = useState<WriteTaskStatus | null>(null);
  const [history, setHistory] = useState<WriteTaskListItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rollingBack, setRollingBack] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 加载任务状态
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const result = await getWriteTaskStatus(writeTaskId);
        setTask(result);

        // 任务完成后停止轮询
        if (result.status !== 'running' && result.status !== 'rolling_back') {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      } catch (e: any) {
        toast.error('获取任务状态失败: ' + e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, 3000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [writeTaskId]);

  // 加载任务历史（基于 write_mapping_id）
  useEffect(() => {
    if (!task) return;
    const fetchHistory = async () => {
      try {
        const result = await getWriteTaskList({
          writeMappingId: (task as any).write_mapping_id,
          page: 1,
          pageSize: 10,
        });
        setHistory(result.items.filter(t => t._id !== writeTaskId));
        setHistoryTotal(result.total);
      } catch { /* ignore */ }
    };
    fetchHistory();
  }, [task?.status, writeTaskId]);

  if (loading) {
    return <div className="p-6 text-muted-foreground">加载中...</div>;
  }

  if (!task) {
    return <div className="p-6 text-destructive">写入任务不存在</div>;
  }

  const canRollback = ['completed', 'partial'].includes(task.status);
  const isRunning = task.status === 'running' || task.status === 'rolling_back';

  const handleRollback = async () => {
    if (!confirm('确认回滚此写入任务？将删除生产库中已写入的记录，此操作不可撤销。')) return;
    setRollingBack(true);
    try {
      await rollbackProdWrite(writeTaskId);
      toast.success('回滚任务已启动');
      // 重新启动轮询
      if (!intervalRef.current) {
        const fetchStatus = async () => {
          const result = await getWriteTaskStatus(writeTaskId);
          setTask(result);
          if (result.status !== 'running' && result.status !== 'rolling_back') {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        };
        intervalRef.current = setInterval(fetchStatus, 3000);
      }
    } catch (e: any) {
      toast.error('回滚失败: ' + e.message);
    } finally {
      setRollingBack(false);
    }
  };
  const processedCount = task.success_count + task.failure_count + task.skip_count;
  const duration = task.end_time
    ? Math.round((task.end_time - task.start_time) / 1000)
    : Math.round((Date.now() - task.start_time) / 1000);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>← 返回</Button>
          <h2 className="text-base font-semibold">写入任务详情</h2>
          <Badge variant={STATUS_VARIANTS[task.status] || 'secondary'}>
            {STATUS_LABELS[task.status] || task.status}
          </Badge>
          {isRunning && (
            <span className="text-xs text-muted-foreground animate-pulse">轮询中...</span>
          )}
        </div>
        {canRollback && (
          <Button
            variant="destructive"
            size="sm"
            onClick={handleRollback}
            disabled={rollingBack}
          >
            {rollingBack ? '回滚中...' : '撤销写入'}
          </Button>
        )}
      </div>

      <div className="p-6 space-y-6">
        {/* 进度条 */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>写入进度</span>
            <span>{task.progress_percent}%</span>
          </div>
          <div className="h-3 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                task.status === 'failed' ? 'bg-destructive' :
                task.status === 'partial' ? 'bg-amber-500' :
                'bg-primary'
              }`}
              style={{ width: `${Math.min(task.progress_percent, 100)}%` }}
            />
          </div>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="总记录数" value={task.total_count} />
          <StatCard label="成功" value={task.success_count} className="text-emerald-600" />
          <StatCard label="失败" value={task.failure_count} className="text-destructive" />
          <StatCard label="跳过" value={task.skip_count} className="text-muted-foreground" />
        </div>

        {/* 任务信息 */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm max-w-lg">
          <span className="text-muted-foreground">写入类型</span>
          <span>{task.write_type === 'full' ? '全量写入' : '增量写入'}</span>
          <span className="text-muted-foreground">开始时间</span>
          <span>{new Date(task.start_time).toLocaleString()}</span>
          {task.end_time && (
            <>
              <span className="text-muted-foreground">结束时间</span>
              <span>{new Date(task.end_time).toLocaleString()}</span>
            </>
          )}
          <span className="text-muted-foreground">耗时</span>
          <span>{duration}s</span>
          <span className="text-muted-foreground">已处理</span>
          <span>{processedCount} / {task.total_count}</span>
        </div>

        {/* 错误信息 */}
        {task.error_message && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive">
            {task.error_message}
          </div>
        )}

        {/* 失败日志 */}
        {task.failures.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">最近失败记录 ({task.failures.length})</h3>
            <div className="border rounded max-h-64 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-48">源记录 ID</TableHead>
                    <TableHead>失败原因</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {task.failures.map((f, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">{f.source_record_id || '-'}</TableCell>
                      <TableCell className="text-xs text-destructive">{f.failure_reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* 历史任务 */}
        {history.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">历史写入任务 ({historyTotal})</h3>
            <div className="border rounded">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>目标表</TableHead>
                    <TableHead>类型</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>成功/失败</TableHead>
                    <TableHead>时间</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map(h => (
                    <TableRow key={h._id}>
                      <TableCell className="font-mono text-xs">{h.prod_table_name}</TableCell>
                      <TableCell>{h.write_type === 'full' ? '全量' : '增量'}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANTS[h.status] || 'secondary'} className="text-xs">
                          {STATUS_LABELS[h.status] || h.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{h.success_count}/{h.failure_count}</TableCell>
                      <TableCell className="text-xs">{new Date(h.start_time).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, className = '' }: { label: string; value: number; className?: string }) {
  return (
    <div className="border rounded p-3 text-center">
      <div className={`text-2xl font-bold tabular-nums ${className}`}>{value.toLocaleString()}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
}
